import React, {
  useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Button, Input, InputRef } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import useCustomTranslation from '@/hooks/useCustomTranslation';
import searchMenuOptions, { TMenuSearchOption } from '@/shared/helpers/searchMenuOptions';

type TMenuSearchProps = {
  // Only the options the user can already see in the menu, so the search never reveals more.
  options: TMenuSearchOption[];
  // Collapsed bar: a magnifier opens the field in a small panel anchored to the side.
  compact: boolean;
  onSelect: (key: string) => void;
};

const PANEL_WIDTH = 296;
const LIST_MAX_HEIGHT = 340;

const MenuSearch = ({ options, compact, onSelect }: TMenuSearchProps) => {
  const { t } = useCustomTranslation();
  const location = useLocation();
  const baseId = useId();
  const listId = `${baseId}-list`;
  const panelId = `${baseId}-panel`;
  const optionId = (index: number) => `${baseId}-option-${index}`;

  const [query, setQuery] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [listStyle, setListStyle] = useState<React.CSSProperties>();
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>();

  const launcherRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<InputRef>(null);
  const openerRef = useRef<HTMLButtonElement>(null);

  const results = useMemo(() => searchMenuOptions(query, options), [query, options]);
  const showList = listOpen && query.trim() !== '';
  const showPanel = compact && panelOpen;

  const close = (restoreFocus = false) => {
    setListOpen(false);
    setActiveIndex(-1);
    if (compact) setPanelOpen(false);
    if (restoreFocus) (compact ? openerRef.current : inputRef.current)?.focus({ preventScroll: true });
  };

  const choose = (index: number) => {
    const option = results[index];
    if (!option) return;
    setQuery('');
    close();
    onSelect(option.key);
  };

  // The list lives in <body> so the sider and drawer scroll areas do not clip it.
  const updateListPosition = useCallback(() => {
    const rect = fieldRef.current?.getBoundingClientRect();
    if (!rect) return;
    setListStyle({
      left: rect.left,
      top: rect.bottom + 4,
      width: rect.width,
      maxHeight: Math.max(80, Math.min(LIST_MAX_HEIGHT, window.innerHeight - rect.bottom - 16)),
    });
  }, []);

  const updatePanelPosition = useCallback(() => {
    const anchor = openerRef.current?.getBoundingClientRect();
    if (!anchor) return;
    const width = Math.min(PANEL_WIDTH, window.innerWidth - 24);
    setPanelStyle({
      width,
      left: Math.max(8, Math.min(anchor.right + 12, window.innerWidth - width - 8)),
      top: Math.max(8, Math.min(anchor.top, window.innerHeight - 64)),
    });
  }, []);

  useLayoutEffect(() => {
    if (showList) updateListPosition();
  }, [showList, panelStyle, updateListPosition]);

  // Keeps the list and panel attached to their anchors while the page scrolls or resizes.
  useEffect(() => {
    if (!showList && !showPanel) return undefined;
    const onViewportChange = (event: Event) => {
      if (event.target instanceof Node && listRef.current?.contains(event.target)) return;
      if (showPanel) updatePanelPosition();
      updateListPosition();
    };
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('scroll', onViewportChange, true);
    return () => {
      window.removeEventListener('resize', onViewportChange);
      window.removeEventListener('scroll', onViewportChange, true);
    };
  }, [showList, showPanel, updateListPosition, updatePanelPosition]);

  // A click or focus outside the field, the panel and the list closes the suggestions.
  useEffect(() => {
    if (!showList && !showPanel) return undefined;
    const onOutside = (event: Event) => {
      const target = event.target as Node;
      if ([launcherRef, panelRef, listRef].some((ref) => ref.current?.contains(target))) return;
      setListOpen(false);
      setActiveIndex(-1);
      setPanelOpen(false);
    };
    document.addEventListener('pointerdown', onOutside);
    document.addEventListener('focusin', onOutside);
    return () => {
      document.removeEventListener('pointerdown', onOutside);
      document.removeEventListener('focusin', onOutside);
    };
  }, [showList, showPanel]);

  // Navigating clears the search; collapsing or expanding the bar closes it.
  useEffect(() => {
    setQuery('');
    setListOpen(false);
    setActiveIndex(-1);
    setPanelOpen(false);
  }, [location.key]);

  useEffect(() => {
    setListOpen(false);
    setActiveIndex(-1);
    setPanelOpen(false);
  }, [compact]);

  useEffect(() => {
    if (showPanel) inputRef.current?.focus({ preventScroll: true });
  }, [showPanel]);

  useEffect(() => {
    if (activeIndex >= 0) document.getElementById(`${baseId}-option-${activeIndex}`)?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, baseId]);

  const onClickOpener = () => {
    if (showPanel) {
      close(true);
      return;
    }
    updatePanelPosition();
    setPanelOpen(true);
    setListOpen(true);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();
        event.stopPropagation();
        if (!query.trim()) return;
        setListOpen(true);
        const count = results.length;
        const current = showList ? activeIndex : -1;
        if (!count) return;
        if (event.key === 'ArrowDown') setActiveIndex(Math.min(current + 1, count - 1));
        else setActiveIndex(current < 0 ? count - 1 : Math.max(current - 1, 0));
        break;
      }
      case 'Enter':
        if (!showList) return;
        event.preventDefault();
        event.stopPropagation();
        choose(activeIndex < 0 ? 0 : activeIndex);
        break;
      case 'Escape':
        if (!showList && !compact) return;
        event.preventDefault();
        event.stopPropagation();
        close(true);
        break;
      case 'Tab':
        if (compact) {
          event.preventDefault();
          close(true);
        } else {
          close();
        }
        break;
      default:
    }
  };

  const label = t('sidebar.search_menu');

  let status = '';
  if (showList) {
    status = results.length ? t('sidebar.search_results_count', { count: results.length }) : t('sidebar.search_no_results');
  }

  const field = (
    <div ref={fieldRef} className="main-nav-search-field">
      <Input
        ref={inputRef}
        prefix={<SearchOutlined aria-hidden />}
        placeholder={label}
        aria-label={label}
        role="combobox"
        aria-autocomplete="list"
        aria-haspopup="listbox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-activedescendant={showList && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        autoComplete="off"
        spellCheck={false}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setListOpen(true);
          setActiveIndex(-1);
        }}
        onFocus={() => {
          if (query.trim()) setListOpen(true);
        }}
        onKeyDown={onKeyDown}
      />
    </div>
  );

  return (
    <>
      <div ref={launcherRef} className={`main-nav-search${compact ? ' main-nav-search-compact' : ''}`}>
        {compact ? (
          <Button
            ref={openerRef}
            className="main-nav-search-opener"
            icon={<SearchOutlined />}
            aria-label={label}
            title={label}
            aria-expanded={showPanel}
            aria-controls={panelId}
            onClick={onClickOpener}
          />
        ) : field}
        <span className="main-nav-search-status" role="status" aria-live="polite" aria-atomic="true">{status}</span>
      </div>
      {showPanel && createPortal(
        <div ref={panelRef} id={panelId} className="main-nav-search-panel" style={panelStyle}>
          {field}
        </div>,
        document.body,
      )}
      {showList && createPortal(
        <div ref={listRef} className="main-nav-search-suggestions" style={listStyle}>
          <div id={listId} role="listbox" aria-label={t('sidebar.search_results_label')}>
            {results.map((option, index) => (
              <div
                key={option.key}
                id={optionId(index)}
                role="option"
                aria-selected={index === activeIndex}
                className="main-nav-search-option"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => choose(index)}
              >
                <span className="main-nav-search-option-label">{option.label}</span>
                <span className="main-nav-search-option-group">{option.groupLabel}</span>
              </div>
            ))}
          </div>
          {!results.length && <p className="main-nav-search-empty">{t('sidebar.search_no_results')}</p>}
        </div>,
        document.body,
      )}
    </>
  );
};

export default MenuSearch;
